import React, { useEffect, useState } from "react"
import Link from "next/link"
import { useTranslation } from "i18n"
import metadata from "metadata.json"

// Material UI
import { makeStyles } from "@material-ui/core/styles"
import {
  Box,
  Card,
  Grid,
  Divider,
  Typography,
  Button,
  useTheme,
  useMediaQuery
} from "@material-ui/core"

// Icons
import ArrowForwardIcon from "@material-ui/icons/ArrowForward"

// Components
import Footer from "components/landing_page/Footer"
import SimpleCard from "components/store/SimpleCard"
import RectangleCard from "components/store/RectangleCard"
import StoreContainer from "components/store/StoreContainer"
import StoreLoader from "components/shared/StoreLoader"

// Helpers
import axios from "shared/axios"
import urls from "shared/urls"
import Metadata from "shared/metadata"
import { getProductPageLink } from "shared/helpersTs"

const useStyles = makeStyles(theme => ({
  divider: {
    margin: theme.spacing(1, 0, 2)
  },
  button: {
    marginTop: theme.spacing(3),
    paddingLeft: theme.spacing(3),
    paddingRight: theme.spacing(3)
  },
  heading: {
    marginTop: theme.spacing(9)
  },
  fontWeightBold: {
    fontWeight: theme.typography.fontWeightBold
  },
  typography: {
    display: "flex",
    alignItems: "center",
    textTransform: "capitalize"
  },
  icon: {
    marginRight: theme.spacing(0.5)
  },
  yellowIcon: {
    color: theme.palette.warning.main
  }
}))

export default function StoreHome() {
  const { t } = useTranslation()

  const theme = useTheme()
  const smDown = useMediaQuery(theme.breakpoints.down("sm"))

  const classes = useStyles()

  const [featuredCourses, setFeaturedCourses] = useState(null)
  const [courses, setCourses] = useState(null)
  const [packages, setPackages] = useState(null)
  const [qualifications, setQualifications] = useState(null)
  const [certifications, setCertifications] = useState(null)

  const getFeaturedCourses = () => {
    axios
      .get(urls.store.courses.list, {
        params: {
          featured: true
        }
      })
      .then(response => {
        setFeaturedCourses(response.data)
      })
      .catch(console.error)
  }

  const getCourses = () => {
    axios
      .get(urls.store.courses.list)
      .then(response => {
        setCourses(response.data)
      })
      .catch(console.error)
  }

  const getPackages = () => {
    axios
      .get(urls.store.packages.list)
      .then(response => {
        setPackages(response.data)
      })
      .catch(console.error)
  }

  const getQualifications = () => {
    axios
      .get(urls.store.qualifications.list, {
        params: {
          fields: ["description", "duration"]
        }
      })
      .then(response => {
        setQualifications(response.data)
      })
      .catch(console.error)
  }

  const getCertifications = () => {
    axios
      .get(urls.store.certifications.list, {
        params: {
          fields: ["description", "duration"]
        }
      })
      .then(response => {
        setCertifications(response.data)
      })
      .catch(console.error)
  }

  useEffect(() => {
    getFeaturedCourses()
    getCourses()
    getPackages()
    getQualifications()
    getCertifications()
  }, [])

  return (
    <>
      <Metadata data={metadata["store"]} />

      <StoreContainer>
        <div className={classes.container}>
          {/* Qualifications */}
          <Box mb={6}>
            <Typography variant="h5" gutterBottom>
              {t("Qualifications")}
            </Typography>
            <Divider className={classes.divider} />

            {qualifications === null ? (
              <StoreLoader />
            ) : qualifications.data.length > 0 ? (
              <Grid container spacing={2}>
                {qualifications.data.map((value, index) => {
                  return (
                    <Link
                      href={getProductPageLink(
                        value.slug,
                        `/qualifications/${value.slug}`
                      )}
                      key={index}
                    >
                      {smDown ? (
                        <Grid item xs={12} sm={6} md={4} lg={3}>
                          <SimpleCard
                            name={value.name}
                            image={value.image}
                            number_of_enrollments={value.number_of_enrollments}
                            rating={value.rating}
                            level={value.qualification_level}
                            number_of_items={value.number_of_courses}
                            duration={value.duration}
                            isEnrolled={value.is_enrolled}
                          />
                        </Grid>
                      ) : (
                        <Grid item xs={12}>
                          <RectangleCard
                            name={value.name}
                            image={value.image}
                            description={value.description}
                            number_of_enrollments={value.number_of_enrollments}
                            rating={value.rating}
                            level={value.qualification_level}
                            number_of_items={value.number_of_courses}
                            duration={value.duration}
                            isEnrolled={value.is_enrolled}
                          />
                        </Grid>
                      )}
                    </Link>
                  )
                })}
              </Grid>
            ) : (
              <ComingSoon />
            )}
          </Box>

          {/* Certifications */}
          <Box mb={6}>
            <Typography variant="h5" gutterBottom>
              {t("Certifications")}
            </Typography>
            <Divider className={classes.divider} />

            {certifications === null ? (
              <StoreLoader />
            ) : certifications.data.length > 0 ? (
              <Grid container spacing={2}>
                {certifications.data.map((value, index) => (
                  <Link
                    href="/certifications/[certificationSlug]"
                    as={`/certifications/${value.slug}`}
                    key={index}
                  >
                    {smDown ? (
                      <Grid item xs={12} sm={6} md={4} lg={3}>
                        <SimpleCard
                          name={value.name}
                          image={value.image}
                          // Price
                          prices={value.products}
                          // Icons
                          number_of_enrollments={value.number_of_enrollments}
                          rating={value.rating}
                          number_of_items={value.number_of_courses}
                          duration={value.duration}
                          isEnrolled={value.is_enrolled}
                        />
                      </Grid>
                    ) : (
                      <Grid item xs={12}>
                        <RectangleCard
                          name={value.name}
                          image={value.image}
                          description={value.description}
                          // Price
                          prices={value.products}
                          // Icons
                          number_of_enrollments={value.number_of_enrollments}
                          rating={value.rating}
                          number_of_items={value.number_of_courses}
                          duration={value.duration}
                          isEnrolled={value.is_enrolled}
                        />
                      </Grid>
                    )}
                  </Link>
                ))}
              </Grid>
            ) : (
              <ComingSoon />
            )}

            {certifications !== null && certifications.data.length > 8 && (
              <Grid container justifyContent="center">
                <Grid item>
                  <Button
                    size="large"
                    variant="contained"
                    color="primary"
                    className={classes.button}
                  >
                    <Link href="/certifications">
                      {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
                      <a>
                        <Typography>{t("View all")}</Typography>
                      </a>
                    </Link>
                    <ArrowForwardIcon fontSize="small" />
                  </Button>
                </Grid>
              </Grid>
            )}
          </Box>

          {/* Featured Courses */}
          <Box mb={6}>
            <Typography variant="h5" gutterBottom>
              {t("Featured Courses")}
              <Divider className={classes.divider} />
            </Typography>

            <Grid container spacing={smDown ? 2 : 4}>
              {featuredCourses !== null ? (
                featuredCourses.data.length > 0 ? (
                  featuredCourses.data
                    .filter((_value, index) => index < 8)
                    .map((value, index) => (
                      <Link
                        href={getProductPageLink(
                          value.slug,
                          `/courses/${value.slug}`
                        )}
                        key={index}
                      >
                        <Grid item xs={12} sm={6} md={4} lg={3}>
                          <SimpleCard
                            name={value.name}
                            image={value.image}
                            // Price
                            prices={value.products}
                            // Icons
                            rating={value.rating}
                            level={value.level}
                            number_of_enrollments={value.number_of_enrollments}
                            learning_mode={value.learning_mode}
                            isEnrolled={value.is_enrolled}
                          />
                        </Grid>
                      </Link>
                    ))
                ) : (
                  <ComingSoon />
                )
              ) : (
                <Grid item xs={12}>
                  <StoreLoader />
                </Grid>
              )}
            </Grid>
          </Box>

          {/* Courses */}
          <Box mb={6}>
            <Typography variant="h5" gutterBottom>
              {t("Courses")}
            </Typography>
            <Divider className={classes.divider} />
            {courses !== null ? (
              courses.data.length > 0 ? (
                <Grid container spacing={smDown ? 2 : 4}>
                  {courses.data
                    .filter((value, index) => index < 8)
                    .map((value, index) => (
                      <Link
                        href={getProductPageLink(
                          value.slug,
                          `/courses/${value.slug}`
                        )}
                        key={index}
                      >
                        <Grid item xs={12} sm={6} md={4} lg={3}>
                          <SimpleCard
                            name={value.name}
                            image={value.image}
                            // Price
                            prices={value.products}
                            // Icons
                            rating={value.rating}
                            level={value.level}
                            number_of_enrollments={value.number_of_enrollments}
                            learning_mode={value.learning_mode}
                            isEnrolled={value.is_enrolled}
                          />
                        </Grid>
                      </Link>
                    ))}
                </Grid>
              ) : (
                <ComingSoon />
              )
            ) : (
              <StoreLoader />
            )}

            {courses !== null && courses.data.length > 8 && (
              <Grid container justifyContent="center">
                <Grid item>
                  <Button
                    size="large"
                    variant="contained"
                    color="primary"
                    className={classes.button}
                  >
                    <Link href="/courses">
                      {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
                      <a>
                        <Typography>{t("View all")}</Typography>
                      </a>
                    </Link>
                    <ArrowForwardIcon fontSize="small"></ArrowForwardIcon>
                  </Button>
                </Grid>
              </Grid>
            )}
          </Box>

          {/* Packages */}
          {/* <Box mb={6}>
            <Typography variant="h5" gutterBottom>
              {t("Packages")}
            </Typography>
            <Divider className={classes.divider} />
            {packages !== null ? (
              packages.data.length > 0 ? (
                <Grid container spacing={smDown ? 2 : 4}>
                  {packages.data
                    .filter((_value, index) => index < 8)
                    .map((value, index) => (
                      <Link
                        href="/packages/[packageSlug]"
                        as={`/packages/${value.slug}`}
                        key={index}
                      >
                        <Grid item xs={12} sm={6} md={4} lg={3}>
                          <SimpleCard
                            name={value.name}
                            image={value.image}
                            // Price
                            prices={value.products}
                            hideLearningModes
                            // Icons
                            rating={value.rating}
                            number_of_enrollments={value.number_of_enrollments}
                            number_of_items={value.number_of_items}
                            isEnrolled={value.is_enrolled}
                          />
                        </Grid>
                      </Link>
                    ))}
                </Grid>
              ) : (
                <ComingSoon />
              )
            ) : (
              <StoreLoader />
            )}
            {packages !== null && packages.data.length > 8 && (
              <Grid container justifyContent="center">
                <Grid item>
                  <Button
                    size="large"
                    variant="contained"
                    color="primary"
                    className={classes.button}
                  >
                    <Link href="/packages">
                      <a>
                        <Typography>{t("View all")}</Typography>
                      </a>
                    </Link>
                    <ArrowForwardIcon fontSize="small"></ArrowForwardIcon>
                  </Button>
                </Grid>
              </Grid>
            )}
          </Box> */}
        </div>
      </StoreContainer>
      <Footer />
    </>
  )
}

function ComingSoon() {
  return (
    <Card className="paper">
      <Typography variant="body1">Coming Soon!</Typography>
    </Card>
  )
}
